import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Link,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Snackbar,
  Alert,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { useState, useEffect } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    type: 'Feedback'
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'faqs'));
      const faqsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFaqs(faqsData);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setFaqsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const contactsRef = collection(db, 'contacts');
      await addDoc(contactsRef, {
        ...formData,
        timestamp: serverTimestamp()
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        type: 'Feedback'
      });
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const faqItems = [
    {
      question: 'How do I submit feedback?',
      answer: 'To submit feedback, please visit our Contact page and fill out the form with your details and the nature of your feedback.'
    },
    {
      question: 'What is the response time for feedback?',
      answer: 'We strive to respond to all feedback within 48 hours. However, response times may vary depending on the complexity of the issue.'
    },
    {
      question: 'Can I remain anonymous?',
      answer: 'Yes, you can choose to remain anonymous when submitting feedback. However, providing your contact information can help us address your concerns more effectively.'
    },
    {
      question: 'What happens after I submit feedback?',
      answer: 'Once you submit your feedback, our team will review it and take appropriate action. We may reach out to you for further information if needed.'
    },
    {
      question: 'How can I track my feedback?',
      answer: 'You can track the status of your feedback by logging into your account and visiting the My Feedback section where you will find updates on the progress of your feedback.'
    },
    {
      question: 'Is my feedback confidential?',
      answer: 'Yes, we treat all feedback with strict confidentiality. Your personal information will only be used for the purpose of addressing your concerns.'
    },
    {
      question: 'Can I provide feedback anonymously?',
      answer: 'Yes, you can choose to provide feedback anonymously. However, providing contact information can help us address your concerns more effectively.'
    }
  ];

  const FaqItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
        <Box
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          <Typography>{question}</Typography>
          <IconButton size="small">
            <CloseIcon sx={{ transform: isOpen ? 'none' : 'rotate(45deg)' }} />
          </IconButton>
        </Box>
        {isOpen && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Typography color="text.secondary" variant="body2">
              {answer}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ mt: '-64px' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '250px' },
          width: '100%',
          overflow: 'hidden',
          mb: 0,
          pt: '64px',
          bgcolor: '#580100',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/contact-banner.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)',
            zIndex: 0
          }
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Contact Us
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  mb: 4,
                  fontWeight: 700,
                  color: '#580100',
                  mt: 8
                }}
              >
                Submit Your Feedback or Complaint
              </Typography>

              <Paper 
                elevation={3} 
                sx={{ 
                  p: { xs: 2, sm: 4 },
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <form onSubmit={handleSubmit}>
                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <RadioGroup
                      row
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      sx={{
                        justifyContent: 'space-around',
                        mb: 2
                      }}
                    >
                      <FormControlLabel 
                        value="Feedback" 
                        control={<Radio />} 
                        label="Feedback"
                      />
                      <FormControlLabel 
                        value="Complaint" 
                        control={<Radio />} 
                        label="Complaint"
                      />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                    variant="outlined"
                  />

                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                    variant="outlined"
                  />

                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                    variant="outlined"
                  />

                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={4}
                    sx={{ mb: 3 }}
                    variant="outlined"
                  />

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem'
                    }}
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </Button>
                </form>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/images/feedback.jpg"
              alt="Feedback"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: 500,
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: 3
              }}
            />
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Your {formData.type.toLowerCase()} has been submitted successfully!
        </Alert>
      </Snackbar>

      {/* FAQs Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom sx={{ color: '#580100', textAlign: 'center', mb: 4, mt: 4 }}>
            FAQs
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 6 }}>
            Find answers to common questions and concerns about the feedback or complaint process.
          </Typography>

          {faqsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {faqs.map((faq) => (
                <Grid item xs={12} md={6} key={faq.id}>
                  <Accordion
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                      '&.Mui-expanded': {
                        margin: 0,
                        mt: 2,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '&.Mui-expanded': {
                          minHeight: 48,
                        },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <Divider />
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Still have questions? */}
      <Container maxWidth="sm" sx={{ textAlign: 'center', mt: 8, mb: 8 }}>
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Still have questions?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Contact our support team for further assistance.
        </Typography>
      </Container>

      {/* Contact Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2, color: '#580100' }}>
          Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          For follow-up or urgent matters, please provide additional contact information.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              {/* Email */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 4 }}>
                <EmailIcon sx={{ color: '#580100', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Send us an email
                  </Typography>
                  <Typography variant="body2">
                  andhericourtsba@gmail.com
                  </Typography>
                </Box>
              </Box>

              {/* Phone */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 4 }}>
                <PhoneIcon sx={{ color: '#580100', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Phone
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Give us a call
                  </Typography>
                  <Typography variant="body2">
                  +91 83568 96595
                  </Typography>
                </Box>
              </Box>

              {/* Office */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 4 }}>
                <LocationOnIcon sx={{ color: '#580100', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Office
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Andheri Court's Bar Association,<br />
                    Court Building, 3rd Floor,<br />
                    Andheri East, Mumbai - 400069
                  </Typography>
                </Box>
              </Box>

       
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              width: '100%', 
              height: '100%',
              minHeight: 350,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3799.1897671276965!2d72.84937219999999!3d19.119116500000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9d30360fbcb%3A0xcce4e87732757597!2sAndheri%20Court!5e1!3m2!1sen!2sin!4v1736580829506!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Andheri Court Location"
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Contact;
